import React, { useState } from "react";
import { Trash2, Download, Pencil, Check, X } from "lucide-react";
import { TaskAttachment } from "../../types";
import { formatFileSize } from "../../lib/storage";
import { getAttachmentIcon } from "./attachmentIcon";
import Tooltip from "../ui/Tooltip";

interface AttachmentListProps {
  attachments: TaskAttachment[];
  onDelete?: (attachmentId: string) => void;
  onRename?: (attachmentId: string, newName: string) => Promise<boolean>;
  /**
   * Opens the given attachment (by its index in `attachments`) in the
   * in-app viewer instead of the browser's own new-tab navigation. When
   * omitted, the file name link falls back to its native `target="_blank"`
   * behavior.
   */
  onOpen?: (index: number) => void;
  /**
   * Index of the attachment the viewer is currently open on, if any — used
   * to suppress that row's "View file" tooltip. The viewer opens as a modal
   * directly under the cursor without the mouse actually moving off the
   * link, so the browser's own hover/focus state on the link never clears
   * naturally while it's covered.
   */
  openIndex?: number | null;
  isDeleting?: string | null;
}

const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  onDelete,
  onRename,
  onOpen,
  openIndex = null,
  isDeleting,
}) => {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [isSavingRename, setIsSavingRename] = useState(false);

  const startRename = (attachment: TaskAttachment) => {
    setRenamingId(attachment.id);
    setRenameValue(attachment.file_name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  const commitRename = async (attachmentId: string) => {
    if (!onRename || !renameValue.trim()) {
      cancelRename();
      return;
    }
    setIsSavingRename(true);
    await onRename(attachmentId, renameValue.trim());
    setIsSavingRename(false);
    setRenamingId(null);
    setRenameValue("");
  };

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="bg-warm-bg-elevated rounded-lg border border-warm-border overflow-hidden">
      {attachments.map((attachment, index) => {
        const IconComponent = getAttachmentIcon(attachment.mime_type);
        const isCurrentlyDeleting = isDeleting === attachment.id;
        const isRenaming = renamingId === attachment.id;

        return (
          <div
            key={attachment.id}
            className={`flex items-center gap-3 px-3 py-1.5 hover:bg-warm-border-subtle transition-colors group ${
              index !== attachments.length - 1
                ? "border-b border-warm-border"
                : ""
            }`}
          >
            <IconComponent
              size={16}
              className="flex-shrink-0 text-warm-text-secondary"
            />
            <div className="flex-1 min-w-0">
              {isRenaming ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename(attachment.id);
                      if (e.key === "Escape") cancelRename();
                    }}
                    aria-label="Rename file"
                    disabled={isSavingRename}
                    autoFocus
                    className="flex-1 min-w-0 text-sm bg-warm-bg-card border border-warm-blue rounded px-1.5 py-0.5 text-warm-text-primary outline-none focus:ring-1 focus:ring-warm-blue"
                  />
                  <button
                    onClick={() => commitRename(attachment.id)}
                    disabled={isSavingRename || !renameValue.trim()}
                    aria-label="Save rename"
                    className="p-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 disabled:opacity-50"
                  >
                    <Check size={13} />
                  </button>
                  <button
                    onClick={cancelRename}
                    disabled={isSavingRename}
                    aria-label="Cancel rename"
                    className="p-1 text-warm-text-tertiary hover:text-warm-text-secondary disabled:opacity-50"
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <Tooltip content="View file" disabled={openIndex === index}>
                  <a
                    href={attachment.storage_url}
                    // A plain click opens the in-app viewer instead of
                    // navigating whenever onOpen is wired up, so this link
                    // no longer really "opens in a new tab" for that case —
                    // dropping target/rel keeps the announced behavior (e.g.
                    // to screen readers) honest. Without onOpen it falls
                    // back to real new-tab navigation, so it keeps both.
                    {...(onOpen
                      ? {}
                      : { target: "_blank", rel: "noopener noreferrer" })}
                    onClick={(e) => {
                      // A plain left click opens the in-app viewer instead;
                      // Cmd/Ctrl/Shift/middle-click keep the browser's own
                      // "open in new tab" behavior via the real href.
                      if (!onOpen || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                        return;
                      }
                      e.preventDefault();
                      onOpen(index);
                    }}
                    className="text-sm text-warm-text-primary hover:text-warm-blue transition-colors cursor-pointer truncate block hover:underline"
                  >
                    {attachment.file_name}
                    <span className="text-xs text-warm-text-tertiary ml-1.5">({formatFileSize(attachment.file_size)})</span>
                  </a>
                </Tooltip>
              )}
            </div>
            {!isRenaming && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <a
                  href={attachment.storage_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={attachment.file_name}
                  aria-label="Download"
                  className="p-1.5 text-warm-text-secondary hover:text-warm-blue transition-colors rounded hover:bg-warm-border-subtle"
                >
                  <Download size={14} />
                </a>
                {onRename && (
                  <button
                    onClick={() => startRename(attachment)}
                    disabled={isCurrentlyDeleting}
                    className="p-1.5 text-warm-text-secondary hover:text-warm-blue transition-colors rounded hover:bg-warm-border-subtle disabled:opacity-50"
                    aria-label="Rename"
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => onDelete(attachment.id)}
                    disabled={isCurrentlyDeleting}
                    className="p-1.5 text-warm-text-secondary hover:text-red-500 transition-colors rounded hover:bg-warm-border-subtle disabled:opacity-50"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AttachmentList;
